import React, { useState } from "react";

import withErrorHandling from "../HOC/ErrorHandling";

import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import Loading from "../components/Loading";

import {
  Alert,
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  useTheme,
} from "@mui/material";

import t from "../helpers/en";
import { authWithCustomLogin, authWithGoogle } from "../helpers/utils";
import { hasValidToken } from "../helpers/tokenStorage";
import {
  glassCard,
  brandButton,
  pageBackground,
  darkSurface,
} from "../helpers/designSystem";

const SIMPLE_LOGIN = "SIMPLE_LOGIN";
const GOOGLE_LOGIN = "GOOGLE_LOGIN";

const Login = (props) => {
  const { onLoginResponse, showError } = props;

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    username: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    username: "",
    password: "",
  });

  const navigate = useNavigate();
  const location = useLocation();

  // Respect the "from" state that Route wrappers (ProtectedRoutes,
  // SsoHandoff) set when they bounce an unauthenticated visit through
  // /login. Both pathname AND search need to survive — SsoHandoff
  // encodes the callback target in ?returnTo=... which is critical for
  // cross-app SSO to hop back to the peer app (e.g. admin.clue2.app).
  //
  // Fallback to "/" when there's no from state (regular login).
  const _from = location?.state?.from;
  // SCK-635 slice B — when a Google-signin bounced here with
  //   ?linkGoogle=1&email=<addr>
  // (from helpers/actions.js on 401 google_link_required), show a
  // banner and, after the password login succeeds, forward to
  // /settings/security so the user can finish linking Google there.
  const searchParams = new URLSearchParams(location.search || "");
  const linkGoogleFlow = searchParams.get("linkGoogle") === "1";
  const linkGoogleEmail = searchParams.get("email") || "";
  const navigate2AfterLogin = linkGoogleFlow
    ? "/settings/security?fromLinkGoogle=1"
    : _from?.pathname && _from.pathname !== "/"
      ? `${_from.pathname}${_from.search || ""}`
      : "/";

  const onChangeTxt = (evt) => {
    const { name, value } = evt.target;
    let cpyFrom = { ...form };
    cpyFrom[name] = value;

    setForm(cpyFrom);
  };

  const onLogin = (btnName, credentialResponse, cb) => {
    if (btnName === SIMPLE_LOGIN) {
      let newErrors = { username: "", password: "" };
      if (!form.username) {
        newErrors.username = "Username is required";
      }
      if (!form.password) {
        newErrors.password = "Password is required";
      }
      if (newErrors.username || newErrors.password) {
        setErrors(newErrors);
        showError("Please fill in all required fields");
        return;
      }
      setErrors({ username: "", password: "" });
    }

    setLoading(true);
    if (btnName === "GOOGLE_LOGIN") {
      authWithGoogle(credentialResponse, (response, status, err) => {
        setLoading(false);
        if (!err) {
          onLoginResponse(response, status, cb);
        } else {
          showError(err);
        }
      });
    } else {
      authWithCustomLogin(credentialResponse, (result, status, err) => {
        setLoading(false);
        if (!err) {
          onLoginResponse(result, status, cb);
        } else {
          showError(err);
        }
      });
    }
  };

  const onToggleShowPassword = () => setShowPassword((show) => !show);

  const onKeyPress = (evt) => {
    if (evt.key === "Enter") {
      evt.preventDefault();

      onLogin("SIMPLE_LOGIN", form, () => {
        navigate(navigate2AfterLogin);
      });
    }
  };

  if (hasValidToken()) {
    // Check VALIDITY, not just presence: SsoHandoff sends you here when
    // its token check fails, and if this guard only checked presence, a
    // stale/expired token in storage would immediately bounce you back
    // to /sso-handoff, which bounces you back here — an infinite loop
    // that never touches the network. Also use navigate2AfterLogin (not
    // location.state.from.pathname alone) so the search string survives
    // — dropping it here strips SsoHandoff's ?returnTo=... too.
    return <Navigate to={navigate2AfterLogin} replace />;
  }

  return (
    <Box
      sx={{
        ...pageBackground(isDarkMode),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem",
      }}
    >
      <Box
        sx={{
          ...glassCard(isDarkMode),
          display: { sm: "flex", md: "grid" },
          gridTemplateColumns: "1fr 1fr",
          gridColumnGap: "1.5rem",
          padding: "2rem",
          width: "fit-content",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
      <span style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <h4 style={{ color: isDarkMode ? darkSurface.text : "#1e293b", margin: "0 0 0.5rem 0" }}>{t.signin}</h4>
        <Typography sx={{ mb: 2, color: isDarkMode ? darkSurface.textMuted : "#64748b" }}>{t.continue2}</Typography>
        {linkGoogleFlow && (
          <Alert
            severity="info"
            sx={{ mb: 2, textAlign: "left" }}
            data-testid="link-google-banner"
          >
            You have an existing account
            {linkGoogleEmail ? (
              <>
                {" "}for <strong>{linkGoogleEmail}</strong>
              </>
            ) : (
              ""
            )}
            . Sign in with your password and we&rsquo;ll help you link Google
            from Settings.
          </Alert>
        )}
      </span>
      <span style={{ display: "grid", gridRowGap: "1rem" }}>
        <div
          style={{ display: "grid", gridRowGap: "1rem" }}
          onKeyDown={onKeyPress}
          tabIndex={0}
        >
          <TextField
            label={t.username}
            name="username"
            value={form.username || ""}
            onChange={onChangeTxt}
            size="small"
            required
            error={!!errors.username}
            helperText={errors.username}
          />
          <TextField
            label={t.password}
            name="password"
            type={showPassword ? "text" : "password"}
            value={form.password || ""}
            onChange={onChangeTxt}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={onToggleShowPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            size="small"
            required
            error={!!errors.password}
            helperText={errors.password}
          />
          <Button
            variant="contained"
            onClick={() => {
              onLogin(SIMPLE_LOGIN, form, () => {
                navigate(navigate2AfterLogin);
              });
            }}
            disabled={loading}
            sx={brandButton()}
          >
            {loading ? <Loading specificDetails={{ size: 24 }} /> : t.signin}
          </Button>
        </div>
        <GoogleLogin
          onSuccess={(credentialResponse) => {
            onLogin(GOOGLE_LOGIN, credentialResponse, () => {
              navigate(navigate2AfterLogin);
            });
          }}
          onError={() => {
            console.error("Login Failed");
          }}
        />
      </span>
      </Box>
    </Box>
  );
};

export default withErrorHandling(Login);
